/**
 * The data layer for the New case wizard's first two steps — read, then
 * confirm. `pipeline.ts` still owns the third step (run).
 *
 * Read (`POST /api/intake/preview`) creates nothing (I1) — see
 * `server/app/routers/intake.py`'s own docstring. It accepts exactly one of a
 * paste, a PDF upload, or a URL to fetch, and hands back three deterministic,
 * model-free readings: is the text legible, what language is it, and what
 * office/grounds/kind a reconstruction of the extraction reads off it. None
 * of that is "the answer" — every field here is a *prefill*, confirmed or
 * corrected at the next step, never trusted on its own.
 *
 * Confirm (`POST /api/matters/{id}/confirm`) is the I1 door: the human accepts
 * or corrects the four fact fields, and only then does the matter move out of
 * `INTAKE`. Nothing in P2's pipeline run will accept an unconfirmed matter
 * (409 `matter_not_confirmed`) — this module is what closes that gate from
 * the UI side.
 */

import { useMutation, type UseMutationResult } from '@tanstack/react-query'

import { api } from './client'
import type { Jurisdiction, Matter } from './types'

// ---------------------------------------------------------------------------
// Vocabulary
// ---------------------------------------------------------------------------

export type IntakeSource = 'paste' | 'upload' | 'url'
export type IntakeMethod = 'paste' | 'text_layer' | 'ocr' | 'none'
export type ReadabilityVerdict = 'READABLE' | 'NO_TEXT' | 'GARBLED' | 'NOT_LATIN_SCRIPT'
export type LanguageDecision = 'DRAFT' | 'DECLINE_NON_ENGLISH' | 'HOLD'

/** `server/app/documents/readability.py`'s `Readability.evidence()`. */
export interface ReadabilityReading {
  vowel_ratio: number
  latin_share: number
  letters: number
  verdict: ReadabilityVerdict
  threshold: number
  threshold_is_calibrated: boolean
  detail: string
}

/** The subset of the language reading the preview response carries. */
export interface LanguageReading {
  code: string | null
  decision: LanguageDecision
  tokens: number
  margin: number
  detail: string
}

/** `server/app/documents/extract.py`'s `OfficeReading.payload()`. */
export interface OfficeReading {
  office: Jurisdiction | null
  scores: Record<string, number>
  margin: number
  confident: boolean
  method: string
}

/** `GroundReading.payload()`. */
export interface GroundReading {
  grounds: string[]
  rejected: string[]
  inferred: boolean
  flags: string[]
  method: string
}

/** `DeadlineReading.payload()` — the reply date the papers print, if any.
 *
 * `printed` is ISO, or null where the document states a period that could not
 * be counted. `derived` says the date was **counted** — "within three months of
 * the Issue date" plus the Issue date — and not read: a US action prints no
 * reply date, and a field labelled "as printed" must not show one. `rejected`
 * names dates that were seen next to a reply cue and deliberately NOT taken —
 * an all-numeric date whose day and month cannot be told apart.
 *
 * `report_date` is the date the document says it was issued (`Issue date:`,
 * the header `Date:`), read off a label and never inferred; `report_quote` is
 * that label and date verbatim. Both are optional so a response from a server
 * that predates them still types. */
export interface DeadlineReading {
  printed: string | null
  quote: string
  rejected: string[]
  flags: string[]
  method: string
  report_date?: string | null
  report_quote?: string
  derived?: boolean
}

/** `MarkReading.payload()` — the mark, including the ones with no words.
 *
 * `text` is null for a purely figurative mark, and that is a fact about the
 * mark rather than a field nobody filled in: `display` carries the sentence to
 * show a person when there is no value to put in the box. */
export interface MarkReading {
  text: string | null
  device: boolean
  display: string
  quote: string
  method: string
}

/** `ProceedingReading.payload()` — which door the document belongs to.
 *
 * A Madrid provisional refusal arrives in one envelope whether the office
 * objected itself or somebody has already opposed the mark, and the refusal
 * type the register prints does not carry the difference. Two of twelve real
 * notifications in the diagnostic sample were oppositions with a named
 * opponent, and nothing here noticed: an attorney read the PDF and re-typed
 * the name. This is that reading — see `server/app/documents/proceeding.py`.
 *
 * `type` is never null. "The document said nothing either way" is the ordinary
 * examination case, so the examination door is the default and `confident` is
 * what carries the difference between a reading and a default — exactly the
 * distinction {@link OfficeReading.confident} draws. `opponent_name` is null on
 * every examination reading and on an opposition whose notice names the party
 * in a shape the reader will not take, which is a real answer and better than
 * a wrong name reaching the conflict check. `quote` is the sentence it came
 * from, verbatim, for the reviewer to search the document for.
 *
 * `opponent_name` being null is not one failure mode — a full sweep of the 554
 * real Madrid refusals in the fixture set found 165 (30%) confidently
 * OPPOSITION with no name this reader could take, and every one checked was
 * EUIPO or UKIPO's own template saying, verbatim, that the opponent's identity
 * went out in a *separate* notice — not a reading that came up short.
 * `opponent_notified_separately` is what tells the two apart: true only on
 * that specific, template-verified shape, so the wizard can say "the
 * companion document has it" instead of a bare "required" that looks
 * identical to an extraction bug. `separately_notified_quote` is the sentence
 * that says so, verbatim — a second quote alongside `quote`, because it is
 * evidence for a different claim (*why* no name was found, not *that* this is
 * an opposition). */
export interface ProceedingReading {
  /** The two spellings `ProceedingType` uses, so the value can be handed
   * straight to `POST /api/matters`. */
  type: 'EXAM_OA' | 'OPPOSITION'
  confident: boolean
  opponent_name: string | null
  opposition_number: string | null
  /** True only when the door is confidently OPPOSITION, no name could be
   * read, *and* the document's own boilerplate says the identity was sent
   * separately (EUIPO, UKIPO) — never a guess. False on every examination
   * reading and on an opposition that names its opponent inline. */
  opponent_notified_separately: boolean
  /** The office's own sentence saying the opponent went out separately.
   * Empty string when `opponent_notified_separately` is false. */
  separately_notified_quote: string
  quote: string
  method: string
}

/** `PartiesReading.payload()` — the holder and the registration number.
 *
 * Both are read from a label on the document and never inferred, so `null` on
 * either means the paper did not print it under one — the field stays the
 * attorney's to type, exactly as before this reading existed. */
export interface PartiesReading {
  holder: string | null
  /** The WIPO international registration number, where the document prints one. */
  registration_number: string | null
  /** The national application number. Separate from `registration_number`
   * rather than replacing it: a Madrid document prints both and they are
   * different facts about one case, so the reading stores each and lets the
   * screen choose. */
  application_number: string | null
  holder_quote: string
  number_quote: string
  application_quote: string
  method: string
}

/** `Extraction.payload()` — office, grounds, kind, deadline, mark, parties and door. */
export interface ExtractionReading {
  office: OfficeReading
  grounds: GroundReading
  kind: 'substantive' | 'specification'
  deadline: DeadlineReading
  mark: MarkReading
  parties: PartiesReading
  proceeding: ProceedingReading
  method: string
  notes: string[]
}

/** What `POST /api/matters/{id}/confirm` needs to attach the source. */
export interface IntakeBlobRef {
  sha256: string
  path: string
  size: number
  mime: string
  page_count: number | null
}

export interface IntakePreviewResponse {
  source: IntakeSource
  method: IntakeMethod
  ocr_used: boolean
  page_count: number | null
  text_excerpt: string
  text_length: number
  readability: ReadabilityReading
  language: LanguageReading
  extraction: ExtractionReading
  blob: IntakeBlobRef | null
}

// ---------------------------------------------------------------------------
// Read
// ---------------------------------------------------------------------------

export type IntakeRequest =
  | { kind: 'paste'; documentText: string }
  | { kind: 'upload'; file: File }
  | { kind: 'url'; documentUrl: string }

function intakeFormData(request: IntakeRequest): FormData {
  const form = new FormData()
  if (request.kind === 'paste') {
    form.set('document_text', request.documentText)
  } else if (request.kind === 'upload') {
    form.set('file', request.file)
  } else {
    form.set('document_url', request.documentUrl)
  }
  return form
}

/**
 * Read a document. No matter is created — see the module docstring.
 *
 * The body is `FormData`, not JSON: {@link api.post} sends it exactly as
 * given (`client.ts`'s `isRawBody`), which is what lets a single endpoint
 * accept a paste, a file and a URL through one multipart contract.
 */
export function useIntakePreview(): UseMutationResult<
  IntakePreviewResponse,
  unknown,
  IntakeRequest
> {
  return useMutation<IntakePreviewResponse, unknown, IntakeRequest>({
    mutationFn: (request) =>
      api.post<IntakePreviewResponse>('/api/intake/preview', intakeFormData(request)),
  })
}

// ---------------------------------------------------------------------------
// Confirm
// ---------------------------------------------------------------------------

export interface ConfirmMatterRequest {
  application_number?: string | null
  mark_text?: string | null
  applicant_name?: string | null
  deadline_date?: string | null
  source_blob_sha256?: string | null
  source_blob_page_count?: number | null
}

/**
 * I1's confirm step. Requires a signed-in person server-side
 * (`current_human` — `server/app/routers/matters.py`); a machine credential
 * gets 403 `human_required`, which surfaces here as an ordinary `ApiError`.
 */
export function useConfirmMatter(): UseMutationResult<
  Matter,
  unknown,
  { matterId: string; body: ConfirmMatterRequest }
> {
  return useMutation<Matter, unknown, { matterId: string; body: ConfirmMatterRequest }>({
    mutationFn: ({ matterId, body }) =>
      api.post<Matter>(`/api/matters/${encodeURIComponent(matterId)}/confirm`, body),
  })
}
